import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { nextAction } from './utils/ssoNext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import { ViewportOrientationProvider } from './context/ViewportOrientationContext';
import AccessDenied from './components/AccessDenied';
import { OverlayHost } from '../../shared/utils/overlays';
import SkipLinks from './components/a11y/SkipLinks';
import { LiveRegion } from './components/a11y/LiveRegion';
import '../../shared/styles/a11y.css';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, maxWidth: 600, margin: '80px auto', fontFamily: 'system-ui, sans-serif' }}>
          <h1 style={{ color: '#dc2626', fontSize: 22 }}>Something went wrong</h1>
          <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
            The app encountered an unexpected error. Please try refreshing the page.
          </p>
          <pre style={{
            background: '#1e293b', color: '#fbbf24', padding: 16, borderRadius: 8,
            fontSize: 12, lineHeight: 1.5, overflow: 'auto', maxHeight: 300,
          }}>
            {String(this.state.error)}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16, background: '#3b82f6', color: '#fff', border: 'none',
              padding: '10px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import SessionHomePage from './pages/SessionHomePage';
import DomainPage from './pages/DomainPage';
import SessionPage from './pages/SessionPage';
import ContentManagerPage from './pages/ContentManagerPage';
import CharacterSelectPage from './pages/CharacterSelectPage';
import DomainMarketplacePage from './pages/DomainMarketplacePage';
import DomainEnrollPage from './pages/DomainEnrollPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import TutorialPlayerPage from './pages/TutorialPlayerPage';
import CertificatePage from './pages/CertificatePage';
import ProgressDashboardPage from './pages/ProgressDashboardPage';
import FlashcardsPage from './pages/FlashcardsPage';
import PuzzlesPage from './pages/PuzzlesPage';
import DifficultyPickerPage from './pages/DifficultyPickerPage';
import ChatMonitorPage from './pages/coach/ChatMonitorPage';
import ModerationQueue from './components/discussions/ModerationQueue';
import CoachAnalyticsPage from './pages/coach/CoachAnalyticsPage';
import CoachUsageReportsPage from './pages/coach/CoachUsageReportsPage';
import ProgressDashboardV2 from './pages/learner/ProgressDashboardV2';
import ResourceLibraryPage from './pages/ResourceLibraryPage';
import SearchResultsPage from './pages/learner/SearchResultsPage';
import MyNotesPage from './pages/learner/MyNotesPage';
import MySubmissionsPage from './pages/learner/MySubmissionsPage';
import EmailPreferencesPage from './pages/learner/EmailPreferencesPage';
import NotificationsPage from './pages/learner/NotificationsPage';
import MobileBottomNav from './components/nav/MobileBottomNav';
import TestDashboardPage from './pages/TestDashboardPage';
import TestSessionPage from './pages/TestSessionPage';
import TestScoreReportPage from './pages/TestScoreReportPage';
import TestReviewPage from './pages/TestReviewPage';
import LearnerManagementPage from './pages/coach/LearnerManagementPage';
import LearnerAdaptiveDetailPage from './pages/coach/LearnerAdaptiveDetailPage';
import ItemBankManagerPage from './pages/coach/ItemBankManagerPage';
import DomainHealthPage from './pages/coach/DomainHealthPage';
import AILHealthPage from './pages/coach/AILHealthPage';
import ReviewCardLibraryPage from './pages/coach/ReviewCardLibraryPage';
import AdaptiveDiagnosticsPage from './pages/coach/AdaptiveDiagnosticsPage';
import FlashcardManagementPage from './pages/coach/FlashcardManagementPage';
import AdminUsersPage from './pages/coach/AdminUsersPage';
import AdminOrgsPage from './pages/coach/AdminOrgsPage';
import AdminBillingPage from './pages/coach/AdminBillingPage';
import SubmissionFeedbackPage from './pages/SubmissionFeedbackPage';

// Phase 2 / Step D: Coach routes have moved to core/ui/coach-app/.
// Coaches sign in at coach.<host> instead of learn.<host>/coach. Any
// learner who hits /coach/* on the learner host gets a friendly bounce
// to the coach hostname (see CoachRedirect below).

function mustSetPassword(learner) {
  return Boolean(learner?.password_change_required);
}

function RequireAuth({ children }) {
  const { isAuthenticated, learner, ssoPending } = useAuth();
  if (ssoPending) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, learner, ssoPending } = useAuth();
  const [params] = useSearchParams();
  if (ssoPending) return null;
  if (isAuthenticated && !mustSetPassword(learner)) {
    // Championship DNA sends signed-out visitors here with ?next=; only safe targets are followed.
    // A 'url' target is on the main host (a different origin): full-page load, not a router navigate.
    const next = nextAction(params.get('next'), window.location.hostname);
    if (next.action === 'assign') {
      window.location.replace(next.to);
      return null;
    }
    return <Navigate to={next.to} replace />;
  }
  return children;
}

function BrandingAuthSync() {
  const { learner } = useAuth();
  const { activeClientId, setClientId } = useBranding();
  useEffect(() => {
    const profileClientId = learner?.client_id || null;
    if (profileClientId && profileClientId !== activeClientId) {
      setClientId(profileClientId);
    }
  }, [learner, activeClientId, setClientId]);
  return null;
}

function RootRedirect() {
  const { isAuthenticated, learner } = useAuth();
  const [homePath, setHomePath] = React.useState('/dashboard');
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    const token = localStorage.getItem('scenario_os_token');
    fetch('/scenario-os/learner/features', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const als = data?.agent_led_sessions;
        if (als?.enabled && als?.home_path) {
          setHomePath(als.home_path);
        } else {
          setHomePath('/dashboard');
        }
      })
      .catch(() => {
        if (!cancelled) setHomePath('/dashboard');
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, learner?.learner_id]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  if (!ready) return null;
  return <Navigate to={homePath} replace />;
}

// Bounce legacy /coach/* URLs to the dedicated coach host. We compute
// the coach hostname by replacing the leading "learn." in the current
// hostname with "coach.". If the hostname doesn't follow the pattern
// (e.g. local dev on localhost), we fall back to AccessDenied with a
// helpful message instead of a redirect loop.
function CoachRedirect() {
  useEffect(() => {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const coachHost = host.startsWith('learn.')
      ? `coach.${host.slice('learn.'.length)}`
      : null;
    if (coachHost) {
      // Strip the /training basename so the coach app's routes resolve correctly.
      // The learner app is mounted at /training (basename) but the coach app is
      // at the root of coach.<host>, so /training/coach/foo must become /coach/foo.
      let path = window.location.pathname;
      if (path.startsWith('/training')) {
        path = path.slice('/training'.length) || '/';
      }
      const rest = path + window.location.search + window.location.hash;
      window.location.replace(
        `${window.location.protocol}//${coachHost}${rest}`,
      );
    }
  }, []);
  return (
    <AccessDenied
      reason="The coach app has moved to its own hostname. If you weren't redirected automatically, please visit https://coach.<your-domain> directly."
    />
  );
}

/** Redirect legacy AI track tutorial URLs to consolidated ``/tutorials/ai/...``. */
function LegacyAiTutorialRedirect({ track }) {
  const { tutorialId } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (track && !params.get('track')) params.set('track', track);
  const qs = params.toString();
  const base = tutorialId
    ? `/tutorials/ai/play/${encodeURIComponent(tutorialId)}`
    : '/tutorials/ai';
  return <Navigate to={`${base}${qs ? `?${qs}` : ''}`} replace />;
}

export default function App() {
  return (
    <AppErrorBoundary>
    <AuthProvider>
      <BrandingProvider>
        <ViewportOrientationProvider>
          <SkipLinks />
          <LiveRegion />
          <BrandingAuthSync />
        {/* basename picks up the `homepage` setting (Vite -> base, kept
            as /training via process.env.PUBLIC_URL by vite.config.js). */}
        <BrowserRouter basename={process.env.PUBLIC_URL || '/'}>
          <div id="main-content" role="main">
          <Routes>
            <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/marketplace" element={<DomainMarketplacePage />} />

            <Route path="/" element={<RootRedirect />} />

            <Route path="/dashboard" element={<RequireAuth><DashboardPage /></RequireAuth>} />
            <Route path="/session-home" element={<RequireAuth><SessionHomePage /></RequireAuth>} />
            <Route path="/character-select/:domainId" element={<RequireAuth><CharacterSelectPage /></RequireAuth>} />
            {/* AI consolidation: track-aware — legacy per-domain URLs redirect to unified AI domain with track param */}
            <Route path="/domain/ai_beginner" element={<Navigate to="/domain/ai?track=ai_fundamentals" replace />} />
            <Route path="/domain/ai_orchestrator" element={<Navigate to="/domain/ai?track=ai_orchestrator_certificate" replace />} />
            <Route path="/domain/ai_entrepreneur" element={<Navigate to="/domain/ai?track=ai_entrepreneur_certificate" replace />} />
            <Route path="/domain/:domain" element={<RequireAuth><DomainPage /></RequireAuth>} />
            <Route path="/session/:domain/:scenarioId" element={<RequireAuth><SessionPage /></RequireAuth>} />
            <Route path="/content-manager/:domain" element={<RequireAuth><ContentManagerPage /></RequireAuth>} />
            <Route path="/enroll/:domainId" element={<RequireAuth><DomainEnrollPage /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><PlayerProfilePage /></RequireAuth>} />
            {/* Legacy AI pack tutorial routes → consolidated domain=ai */}
            <Route path="/tutorials/ai_beginner" element={<LegacyAiTutorialRedirect track="ai_fundamentals" />} />
            <Route path="/tutorials/ai_beginner/play/:tutorialId" element={<LegacyAiTutorialRedirect track="ai_fundamentals" />} />
            <Route path="/tutorials/ai_orchestrator" element={<LegacyAiTutorialRedirect track="ai_orchestrator_certificate" />} />
            <Route path="/tutorials/ai_orchestrator/play/:tutorialId" element={<LegacyAiTutorialRedirect track="ai_orchestrator_certificate" />} />
            <Route path="/tutorials/ai_entrepreneur" element={<LegacyAiTutorialRedirect track="ai_entrepreneur_certificate" />} />
            <Route path="/tutorials/ai_entrepreneur/play/:tutorialId" element={<LegacyAiTutorialRedirect track="ai_entrepreneur_certificate" />} />
            <Route path="/tutorials/:domain" element={<RequireAuth><TutorialPlayerPage /></RequireAuth>} />
            <Route path="/tutorials/:domain/play/:tutorialId" element={<RequireAuth><TutorialPlayerPage /></RequireAuth>} />
            <Route path="/dashboard/certificate/:domain" element={<RequireAuth><CertificatePage /></RequireAuth>} />
            <Route path="/dashboard/progress" element={<RequireAuth><ProgressDashboardPage /></RequireAuth>} />
            <Route path="/flashcards/:domain" element={<RequireAuth><FlashcardsPage /></RequireAuth>} />
            <Route path="/puzzles/:domain" element={<RequireAuth><PuzzlesPage /></RequireAuth>} />
            {/* Legacy flashcard path used by older launch payloads */}
            <Route path="/dashboard/review" element={<Navigate to="/flashcards/ai" replace />} />
            <Route path="/dashboard/progress-v2" element={<RequireAuth><ProgressDashboardV2 /></RequireAuth>} />
            <Route path="/dashboard/resources" element={<RequireAuth><ResourceLibraryPage /></RequireAuth>} />
            <Route path="/dashboard/notes" element={<RequireAuth><MyNotesPage /></RequireAuth>} />
            <Route path="/my-notes" element={<RequireAuth><MyNotesPage /></RequireAuth>} />
            <Route path="/search" element={<RequireAuth><SearchResultsPage /></RequireAuth>} />
            <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
            <Route path="/settings/email-preferences" element={<RequireAuth><EmailPreferencesPage /></RequireAuth>} />
            <Route path="/my/submissions" element={<RequireAuth><MySubmissionsPage /></RequireAuth>} />
            <Route path="/my/submissions/:submissionId" element={<RequireAuth><SubmissionFeedbackPage /></RequireAuth>} />
            <Route path="/dashboard/domain/:domain/difficulty" element={<RequireAuth><DifficultyPickerPage /></RequireAuth>} />

            {/* Test Prep routes */}
            <Route path="/tests/:domain" element={<RequireAuth><TestDashboardPage /></RequireAuth>} />
            <Route path="/tests/:domain/session/:testId" element={<RequireAuth><TestSessionPage /></RequireAuth>} />
            <Route path="/tests/:domain/report/:sessionId" element={<RequireAuth><TestScoreReportPage /></RequireAuth>} />
            <Route path="/tests/:domain/review/:sessionId" element={<RequireAuth><TestReviewPage /></RequireAuth>} />

            {/* Coach pages — must appear before the /coach/* catch-all. */}
            <Route path="/coach/chats" element={<RequireAuth><ChatMonitorPage /></RequireAuth>} />
            <Route path="/coach/moderation" element={<RequireAuth><ModerationQueue /></RequireAuth>} />
            <Route path="/coach/analytics" element={<RequireAuth><CoachAnalyticsPage /></RequireAuth>} />
            <Route path="/coach/usage" element={<RequireAuth><CoachUsageReportsPage /></RequireAuth>} />
            <Route path="/coach/learners" element={<RequireAuth><LearnerManagementPage /></RequireAuth>} />
            <Route path="/coach/learners/:learnerId/adaptive" element={<RequireAuth><LearnerAdaptiveDetailPage /></RequireAuth>} />
            <Route path="/coach/workspace/:domain/item-bank" element={<RequireAuth><ItemBankManagerPage /></RequireAuth>} />
            <Route path="/coach/workspace/:domain/health" element={<RequireAuth><DomainHealthPage /></RequireAuth>} />
            <Route path="/coach/workspace/:domain/review-cards" element={<RequireAuth><ReviewCardLibraryPage /></RequireAuth>} />
            <Route path="/coach/flashcards" element={<RequireAuth><FlashcardManagementPage /></RequireAuth>} />
            <Route path="/coach/platform/ail-health" element={<RequireAuth><AILHealthPage /></RequireAuth>} />
            <Route path="/coach/admin/adaptive-diagnostics" element={<RequireAuth><AdaptiveDiagnosticsPage /></RequireAuth>} />
            <Route path="/coach/admin/users" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />
            <Route path="/coach/admin/organizations" element={<RequireAuth><AdminOrgsPage /></RequireAuth>} />
            <Route path="/coach/admin/billing" element={<RequireAuth><AdminBillingPage /></RequireAuth>} />

            {/* Legacy /coach/* paths bounce to coach.<host>. */}
            <Route path="/coach/*" element={<CoachRedirect />} />

            <Route path="*" element={<RootRedirect />} />
          </Routes>
          <MobileBottomNav />
          </div>
        </BrowserRouter>
        {/* Phase 2 / Step E — global toast + confirm host. Anywhere in
            the tree can call toast.success(...) or await confirm({...}). */}
        <OverlayHost />
        </ViewportOrientationProvider>
      </BrandingProvider>
    </AuthProvider>
    </AppErrorBoundary>
  );
}
